import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/api';
import WhatChanged from '../components/sap/WhatChanged';
import { SapObjectLabel } from '../components/sap/sapObjectNames';

/**
 * Live view of what SAP is sending us.
 *
 * The failure this screen exists to catch is silence. When a webhook stops being
 * delivered nothing errors — it looks exactly like a quiet day — which is how BOM
 * and item drift went unnoticed for months before. So the headline number here is
 * how long it has been since the last event, not how many arrived.
 */

const CARD = 'bg-white border border-gray-200 rounded-lg p-4';
const TH = 'px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 whitespace-nowrap';
const TD = 'px-3 py-2 text-sm text-gray-700 border-t border-gray-100 align-top';

// SAP is quiet outside working hours, so a gap only means trouble once it is long
// enough that a working day could not explain it.
const QUIET_WARN_MIN = 120;
const QUIET_BAD_MIN = 480;

/**
 * How worrying is this silence, for this kind of document?
 *
 * A flat threshold cannot work here. Production orders arrive every few minutes and
 * purchase returns every couple of days, so eight hours of quiet means something is
 * broken in the first case and nothing at all in the second. Judged flatly, the four
 * rarest document types sat permanently on "Check" — which trains people to ignore
 * the column, and a warning nobody reads cannot warn about anything.
 *
 * `typicalGap` is what the server measured for this object over the last month. Quiet
 * for three times its own rhythm is worth a look; ten times is worth acting on. The
 * floor stops a chatty object being flagged over a normal lunch break, and without a
 * measured rhythm it falls back to the old flat rule.
 */
function silenceTone(minutes, typicalGap) {
  if (minutes == null) return 'none';
  if (!typicalGap) {
    if (minutes >= QUIET_BAD_MIN) return 'bad';
    if (minutes >= QUIET_WARN_MIN) return 'warn';
    return 'ok';
  }
  const warnAt = Math.max(typicalGap * 3, QUIET_WARN_MIN);
  const badAt = Math.max(typicalGap * 10, QUIET_BAD_MIN);
  if (minutes >= badAt) return 'bad';
  if (minutes >= warnAt) return 'warn';
  return 'ok';
}

/** Plain words for how often this kind of document normally turns up. */
function cadenceLabel(minutes) {
  if (!minutes) return null;
  if (minutes < 60) return `about every ${Math.round(minutes)} min`;
  const h = minutes / 60;
  if (h < 48) return `about every ${h < 10 ? h.toFixed(1) : Math.round(h)} hr`;
  return `about every ${Math.round(h / 24)} days`;
}

function humanGap(minutes) {
  if (minutes == null) return 'never';
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const h = Math.floor(minutes / 60);
  if (h < 24) return `${h} hr ago`;
  return `${Math.floor(h / 24)} day${Math.floor(h / 24) === 1 ? '' : 's'} ago`;
}

function Pill({ tone, children }) {
  const tones = {
    ok: 'bg-green-50 text-green-700 border-green-200',
    warn: 'bg-amber-50 text-amber-700 border-amber-200',
    bad: 'bg-red-50 text-red-700 border-red-200',
    none: 'bg-gray-100 text-gray-500 border-gray-200',
    info: 'bg-blue-50 text-blue-700 border-blue-200',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded border text-xs font-medium ${tones[tone] || tones.none}`}>
      {children}
    </span>
  );
}

const STATUS_TONE = {
  done: 'ok', pending: 'info', processing: 'info', failed: 'bad', skipped: 'none',
};

export default function SapWebhookMonitor() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastLoaded, setLastLoaded] = useState(null);
  // Out of the page body, into a corner: failures are rare and the screen is read
  // every day for the ordinary case. A red panel permanently in the flow trains
  // people to scroll past the one thing that needs acting on.
  const [showFailures, setShowFailures] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/sap/webhook/status');
      setData(res);
      setError('');
      setLastLoaded(new Date());
    } catch (e) {
      setError(e.message || 'Could not load webhook status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!autoRefresh) {
      if (timer.current) clearInterval(timer.current);
      return undefined;
    }
    timer.current = setInterval(load, 10000);
    return () => timer.current && clearInterval(timer.current);
  }, [autoRefresh, load]);

  const live = (data && data.live) || {};
  const queueBy = {};
  for (const row of (data && data.queue) || []) queueBy[row.status] = Number(row.count);

  const latestMinutes = live.latest
    ? Math.floor((Date.now() - new Date(live.latest).getTime()) / 60000)
    : null;
  const overallTone = silenceTone(latestMinutes);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">SAP Live Updates</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Changes made in SAP arrive here within seconds — no waiting for the next sync.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300"
            />
            Auto-refresh
          </label>
          <button
            onClick={load}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && !data && <div className="text-sm text-gray-500">Loading…</div>}

      {data && (
        <>
          {/* Headline: is anything arriving at all? */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <div className={CARD}>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Last update from SAP</div>
              <div className="mt-1 text-2xl font-semibold text-gray-900">{humanGap(latestMinutes)}</div>
              <div className="mt-2">
                <Pill tone={overallTone}>
                  {overallTone === 'ok' ? 'Receiving normally'
                    : overallTone === 'warn' ? 'Quiet for a while'
                      : overallTone === 'bad' ? 'No updates — check integration'
                        : 'Nothing received yet'}
                </Pill>
              </div>
            </div>
            <div className={CARD}>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Last hour</div>
              <div className="mt-1 text-2xl font-semibold text-gray-900">{Number(live.last_hour || 0)}</div>
              <div className="text-xs text-gray-500 mt-2">updates received</div>
            </div>
            <div className={CARD}>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Last 24 hours</div>
              <div className="mt-1 text-2xl font-semibold text-gray-900">{Number(live.last_24h || 0)}</div>
              <div className="text-xs text-gray-500 mt-2">updates received</div>
            </div>
            <div className={CARD}>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Waiting / failed</div>
              <div className="mt-1 text-2xl font-semibold text-gray-900">
                {(queueBy.pending || 0) + (queueBy.processing || 0)}
                {queueBy.failed ? <span className="text-red-600"> / {queueBy.failed}</span> : null}
              </div>
              <div className="text-xs text-gray-500 mt-2">
                {queueBy.failed ? 'failures need attention' : 'nothing stuck'}
              </div>
            </div>
          </div>

          {/* Per data type */}
          <div className={`${CARD} mb-5`}>
            <h2 className="text-sm font-semibold text-gray-900 mb-3">By data type</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className={TH}>Data from SAP</th>
                    <th className={TH}>Last received</th>
                    <th className={TH}>Total</th>
                    <th className={TH}>Failed now</th>
                    <th className={TH}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.subscriptions || []).length === 0 && (
                    <tr><td className={TD} colSpan={5}>No updates received yet.</td></tr>
                  )}
                  {(data.subscriptions || []).map((s) => {
                    const tone = silenceTone(s.minutes_since_last_event, s.typical_gap_minutes);
                    const rhythm = cadenceLabel(s.typical_gap_minutes);
                    return (
                      <tr key={s.business_object}>
                        <td className={TD}><SapObjectLabel name={s.business_object} /></td>
                        <td className={TD}>
                          {humanGap(s.minutes_since_last_event)}
                          {/* Without this, a gap of two days looks alarming even when
                              this document only ever appears every two days. */}
                          {rhythm && (
                            <div className="text-xs text-gray-400">{rhythm}</div>
                          )}
                        </td>
                        <td className={TD}>{s.events_received}</td>
                        {/* events_failed counts every failed attempt ever and is never
                            cleared, so a retry that succeeded still shows there. What
                            matters is how many are stuck failed right now; the lifetime
                            number stays as muted context so it cannot read as breakage. */}
                        <td className={TD}>
                          {Number(s.failed_now) > 0
                            ? <span className="text-red-600 font-medium">{s.failed_now}</span>
                            : '0'}
                          {Number(s.events_failed) > 0 && (
                            <div className="text-xs text-gray-400">
                              {s.events_failed} retried since start
                            </div>
                          )}
                        </td>
                        <td className={TD}>
                          <Pill tone={tone}>
                            {tone === 'ok' ? 'Healthy' : tone === 'warn' ? 'Quiet' : tone === 'bad' ? 'Check' : '—'}
                          </Pill>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Failures live behind the corner button below, not in the page flow. */}

          {/* Recent activity */}
          <div className={CARD}>
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Recent updates</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className={TH}>Data</th>
                    <th className={TH}>Change</th>
                    <th className={TH}>Record</th>
                    <th className={TH}>What we saved</th>
                    <th className={TH}>Status</th>
                    <th className={TH}>Received</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.recent || []).length === 0 && (
                    <tr><td className={TD} colSpan={6}>Nothing yet.</td></tr>
                  )}
                  {(data.recent || []).map((r) => (
                    <tr key={r.event_id}>
                      <td className={TD}><SapObjectLabel name={r.business_object} /></td>
                      <td className={TD}>{r.transaction_type}</td>
                      <td className={`${TD} font-mono text-xs`}>
                        {r.record_label || (r.field_names ? `${r.field_names} ${r.field_values}` : '—')}
                      </td>
                      <td className={TD}><WhatChanged row={r} /></td>
                      <td className={TD}>
                        <Pill tone={STATUS_TONE[r.status] || 'none'}>{r.status}</Pill>
                        {r.last_error && (
                          <div className="text-xs text-red-600 mt-1 max-w-md">{r.last_error}</div>
                        )}
                      </td>
                      <td className={TD}>{new Date(r.received_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-4">
            {lastLoaded ? `Refreshed ${lastLoaded.toLocaleTimeString()}` : ''}
            {autoRefresh ? ' · updating every 10 seconds' : ''}
          </p>
        </>
      )}
      {/* A quiet marker when all is well, a red count when it is not. */}
      {data && (
        <>
          <button
            type="button"
            onClick={() => setShowFailures((v) => !v)}
            className={`fixed bottom-5 right-5 z-40 flex items-center gap-2 px-3.5 py-2 rounded-full shadow-lg
              text-xs font-semibold transition-colors ${
              (data.failures || []).length > 0
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
            }`}
            title={(data.failures || []).length > 0
              ? 'Updates from SAP that did not save. Click to see why.'
              : 'Nothing from SAP has failed to save.'}
          >
            {(data.failures || []).length > 0 ? (
              <>
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-white" />
                {data.failures.length} failed update{data.failures.length === 1 ? '' : 's'}
              </>
            ) : (
              <>
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500" />
                No failures
              </>
            )}
          </button>

          {showFailures && (
            <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
              <button
                type="button"
                aria-label="Close"
                className="absolute inset-0 bg-black/20"
                onClick={() => setShowFailures(false)}
              />
              <div className="relative w-full max-w-3xl h-full bg-white shadow-2xl overflow-y-auto">
                <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-red-700">
                    Failed updates ({(data.failures || []).length})
                  </h2>
                  <button
                    type="button"
                    onClick={() => setShowFailures(false)}
                    className="text-gray-400 hover:text-gray-700 text-xl leading-none px-2"
                  >
                    &times;
                  </button>
                </div>
                <div className="p-5">
                  {(data.failures || []).length === 0 ? (
                    <p className="text-sm text-gray-500">
                      Nothing from SAP has failed to save.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead>
                          <tr>
                            <th className={TH}>Data</th>
                            <th className={TH}>Change</th>
                            <th className={TH}>Tries</th>
                            <th className={TH}>Reason</th>
                            <th className={TH}>Received</th>
                          </tr>
                        </thead>
                        <tbody>
                    {data.failures.map((f) => (
                      <tr key={f.event_id}>
                        <td className={TD}><SapObjectLabel name={f.business_object} /></td>
                        <td className={TD}>{f.transaction_type}</td>
                        <td className={TD}>{f.attempts}</td>
                        <td className={`${TD} text-red-700`}>{f.last_error}</td>
                        <td className={TD}>{new Date(f.received_at).toLocaleString()}</td>
                      </tr>
                    ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
